'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { Star, TrendingUp, Users, Zap } from 'lucide-react';
import { Navbar } from '@/components/landing/Navbar';
import { Footer } from '@/components/landing/Footer';

export default function CustomersPage() {
    const testimonials = [
        {
            name: 'Sarah Chen',
            role: 'Founder, TechStart',
            quote: "Honestly, I was skeptical about 'AI marketing' at first. But Revenue Guard caught a $2k ad spend leak on a Sunday morning while I was asleep. That one notification paid for the subscription for the next ten years. It's not just a tool; it's peace of mind.",
            metric: 'Saved $15k in Month 1'
        },
        {
            name: 'Marcus Thorne',
            role: 'Head of Growth, Velos',
            quote: "We used to spend hours every week just staring at spreadsheets, trying to figure out why our ROAS dipped. Axist just tells us. 'Creative fatigue on Ad Set B.' Done. We swapped the creative, and ROAS jumped back up to 4.5x. It's like having a data scientist in my pocket.",
            metric: '4.5x ROAS Achieved'
        },
        {
            name: 'Elena Rodriguez',
            role: 'CMO, Luma Beauty',
            quote: "The Creative Studio is actually scary good. I put in a few bullet points about our new serum, and it spat out ad copy that beat our agency's control by 40%. It captures our brand voice perfectly, which is something I've never seen an AI do before.",
            metric: '+40% CTR vs Agency'
        },
        {
            name: 'David Kim',
            role: 'E-commerce Manager, HypeStreet',
            quote: "Connecting Shopify and Meta Ads in one place is a game changer. I can finally see the real impact of our top-of-funnel campaigns without getting lost in attribution hell. Axist connects the dots that Google Analytics misses.",
            metric: '100% Attribution Clarity'
        },
        {
            name: 'Priya Patel',
            role: 'Director of Marketing, Scribe',
            quote: "I love that it doesn't just dump data on me. It gives me *actions*. 'Increase budget on Campaign X because it's trending up.' I just click 'Approve' and get back to strategy. It's removed so much decision fatigue from my day.",
            metric: '10hrs/week Saved'
        },
        {
            name: 'Tom O\'Malley',
            role: 'Founder, BrewBox',
            quote: "As a solo founder, I don't have time to be a media buyer. Axist basically runs my ads for me. It scales winners and kills losers faster than I ever could manually. It's the only reason we hit our Q4 targets.",
            metric: '2x Revenue Growth'
        }
    ];

    const stats = [
        { icon: Users, value: '500+', label: 'Active Users' },
        { icon: TrendingUp, value: '$2M+', label: 'Ad Spend Optimized' },
        { icon: Zap, value: '10K+', label: 'Campaigns Managed' },
        { icon: Star, value: '4.9/5', label: 'Customer Rating' }
    ];

    return (
        <div className="min-h-screen bg-white">
            <Navbar />

            <main className="pt-24 pb-20">
                {/* Hero Section */}
                <section className="max-w-7xl mx-auto px-6 py-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center max-w-3xl mx-auto"
                    >
                        <h1 className="text-5xl font-bold text-gray-900 mb-6">
                            Trusted by Growth Teams <span className="text-orange-600">Worldwide</span>
                        </h1>
                        <p className="text-xl text-gray-600">
                            Join hundreds of companies using Axist to scale their revenue operations
                        </p>
                    </motion.div>
                </section>

                {/* Stats Section */}
                <section className="max-w-7xl mx-auto px-6 py-12">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {stats.map((stat, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="text-center"
                            >
                                <div className="inline-flex items-center justify-center w-12 h-12 bg-orange-100 rounded-xl mb-4">
                                    <stat.icon className="text-orange-600" size={24} />
                                </div>
                                <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                                <div className="text-sm text-gray-600">{stat.label}</div>
                            </motion.div>
                        ))}
                    </div>
                </section>

                {/* Testimonials Section */}
                <section className="max-w-7xl mx-auto px-6 py-16">
                    <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
                        What Our Customers Say
                    </h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        {testimonials.map((testimonial, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="bg-gray-50 rounded-2xl p-8 border border-gray-200"
                            >
                                <div className="mb-6">
                                    <div className="font-semibold text-gray-900 text-lg">{testimonial.name}</div>
                                    <div className="text-sm text-gray-500">{testimonial.role}</div>
                                </div>
                                <p className="text-gray-700 mb-6 leading-relaxed">"{testimonial.quote}"</p>
                                <div className="inline-block px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold">
                                    {testimonial.metric}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </section>

                {/* CTA Section */}
                <section className="max-w-4xl mx-auto px-6 py-16">
                    <div className="bg-gradient-to-r from-orange-600 to-orange-500 rounded-2xl p-12 text-center text-white">
                        <h2 className="text-3xl font-bold mb-4">Ready to Join Them?</h2>
                        <p className="text-orange-100 mb-8 text-lg">
                            Start optimizing your revenue operations today
                        </p>
                        <a
                            href="/login?mode=signup"
                            className="inline-block px-8 py-4 bg-white text-orange-600 font-semibold rounded-full hover:bg-gray-100 transition-colors"
                        >
                            Get Started Free
                        </a>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}
